import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { HiArrowLeft, HiMail, HiPhone, HiLocationMarker, HiUsers, HiOfficeBuilding } from 'react-icons/hi';
import { Button, Card, CardHeader, Table, Badge } from '../components/ui';
import { getOwner } from '../services/owners.api';
import { formatDate } from '../utils/helpers';

export function OwnerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: owner, isLoading } = useQuery({
    queryKey: ['owner', id],
    queryFn: () => getOwner(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!owner) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Owner not found</p>
        <Button variant="secondary" onClick={() => navigate('/owners')} className="mt-4">
          Back to Owners
        </Button>
      </div>
    );
  }

  const partnerColumns = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    {
      key: 'isActive',
      header: 'Status',
      render: (p: { isActive: boolean }) => (
        <Badge status={p.isActive ? 'ACTIVE' : 'SUSPENDED'}>
          {p.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'customers',
      header: 'Customers',
      render: (p: { _count?: { customers: number } }) => p._count?.customers ?? 0,
    },
  ];

  const userColumns = [
    { key: 'email', header: 'Email' },
    {
      key: 'name',
      header: 'Name',
      render: (u: { firstName: string; lastName: string }) => `${u.firstName} ${u.lastName}`,
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (u: { isActive: boolean }) => (
        <Badge status={u.isActive ? 'ACTIVE' : 'SUSPENDED'}>
          {u.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate('/owners')} className="mb-4">
        <HiArrowLeft className="w-4 h-4 mr-2" />
        Back to Owners
      </Button>

      <CardHeader title={owner.name} description="Owner details, partners and access" />

      {/* Owner Info */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiMail className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{owner.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiPhone className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="font-medium">{owner.phone || '-'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiLocationMarker className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Address</p>
              <p className="font-medium">{owner.address || '-'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <HiOfficeBuilding className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <Badge status={owner.isActive ? 'ACTIVE' : 'SUSPENDED'}>
                {owner.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-6 text-sm text-gray-500">
          <span>Created: {formatDate(owner.createdAt)}</span>
          <span>Partners: {owner._count?.partners ?? 0}</span>
          <span>Users: {owner._count?.users ?? 0}</span>
        </div>
      </Card>

      {/* Partners */}
      <Card className="mb-6" padding={false}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <HiUsers className="w-5 h-5 text-gray-400" />
            Partners ({owner.partners?.length || 0})
          </h3>
        </div>
        <Table
          columns={partnerColumns}
          data={owner.partners || []}
          keyExtractor={(partner) => partner.id}
          emptyMessage="No partners assigned to this owner"
          onRowClick={(partner) => navigate(`/partners/${partner.id}`)}
        />
      </Card>

      {/* Users with access */}
      <Card padding={false}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <HiUsers className="w-5 h-5 text-gray-400" />
            Users mit Zugriff ({owner.users?.length || 0})
          </h3>
        </div>
        <Table
          columns={userColumns}
          data={owner.users || []}
          keyExtractor={(user) => user.id}
          emptyMessage="Kein Benutzer mit diesem Owner verknüpft."
        />
      </Card>
    </div>
  );
}
